import { useState } from "react";
import { Languages, Volume2, Users, Puzzle, Type, Contrast, BookOpenText, Link2, Palette, Gauge } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const TOGGLES = [
  { id: "big", label: "Bigger text", icon: Type },
  { id: "contrast", label: "High contrast", icon: Contrast },
  { id: "dyslexia", label: "Dyslexia-friendly font", icon: BookOpenText },
  { id: "links", label: "Highlight links", icon: Link2 },
  { id: "gray", label: "Grayscale", icon: Palette },
  { id: "spacing", label: "More spacing", icon: Gauge },
];

const ToggleRow = ({ t, on, onToggle }) => (
  <button
    data-testid={`toolbar-toggle-${t.id}`}
    onClick={onToggle}
    role="switch"
    aria-checked={on}
    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-left transition-all ${
      on ? "border-cy/50 bg-cy/10" : "border-white/10 bg-ink hover:border-white/25"
    }`}
  >
    <span className="flex items-center gap-3 text-sm font-medium text-white">
      <t.icon className={`h-4 w-4 ${on ? "text-cy" : "text-slate-500"}`} />
      {t.label}
    </span>
    <span className={`relative h-5 w-9 rounded-full transition-colors ${on ? "bg-cy" : "bg-white/15"}`}>
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[1.15rem]" : "left-0.5"}`}
      />
    </span>
  </button>
);

const Preview = ({ s }) => (
  <div
    data-testid="toolbar-preview-card"
    className={`overflow-hidden rounded-2xl border transition-all duration-500 ${
      s.contrast ? "border-yellow-400/60 bg-black text-white" : "border-white/10 bg-white text-slate-900"
    } ${s.gray ? "grayscale" : ""}`}
  >
    <div className={`flex items-center justify-between px-5 py-3 text-[11px] font-bold tracking-widest ${s.contrast ? "text-yellow-300" : "text-slate-500"}`}>
      <span>ROSIE'S BAKERY</span>
      <span className="flex gap-3">
        <span>Menu</span>
        <span>Visit</span>
      </span>
    </div>
    <div className={`px-5 pb-6 ${s.big ? "text-lg" : ""} ${s.dyslexia ? "[font-family:Verdana,ui-sans-serif] tracking-wide" : ""} ${s.spacing ? "leading-loose tracking-wide" : "leading-normal"}`}>
      <h4 className={`font-bold ${s.big ? "text-3xl" : "text-xl"}`}>Fresh bread, every morning.</h4>
      <p className="mt-2 text-sm sm:text-base">
        Every loaf is shaped by hand and baked before sunrise.{" "}
        <span
          className={`rounded transition-all duration-300 ${
            s.links ? "bg-yellow-300 text-black underline font-semibold" : s.contrast ? "text-yellow-300 underline" : "underline"
          }`}
        >
          See today's menu
        </span>{" "}
        or read our story.
      </p>
      <button className={`mt-4 rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${s.contrast ? "bg-yellow-300 text-black" : "bg-slate-900 text-white"}`}>
        Order pickup
      </button>
      <div className={`mt-5 h-1.5 w-full overflow-hidden rounded-full ${s.contrast ? "bg-white/20" : "bg-slate-200"}`}>
        <div className={`h-full w-1/3 rounded-full ${s.gray ? "bg-slate-500" : "bg-cyan-500"} ${s.contrast ? "bg-yellow-400" : ""}`} />
      </div>
    </div>
  </div>
);

export const ToolbarPlayground = () => {
  const [state, setState] = useState({ big: false, contrast: false, dyslexia: false, links: false, gray: false, spacing: false });
  const flip = (id) => setState((s) => ({ ...s, [id]: !s[id] }));

  return (
    <section id="toolbar" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>TRY THE TOOLBAR — RIGHT NOW</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">
            FLIP A SWITCH.
            <br />
            <span className="text-cy">FEEL THE DIFFERENCE.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-300">
            This is a taste of what your visitors get: one-tap profiles, read-aloud in four ways, 132 languages — 84
            features live in every plan.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal delay={0.05}>
            <div className="space-y-3 rounded-3xl border border-white/10 bg-panel p-5 sm:p-6">
              <p className="font-mono text-[11px] tracking-[0.22em] text-slate-500">ACCESSIBILITY TOOLBAR</p>
              {TOGGLES.map((t) => (
                <ToggleRow key={t.id} t={t} on={state[t.id]} onToggle={() => flip(t.id)} />
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-ink/60 p-5 sm:p-6">
              <p className="font-mono text-[11px] tracking-[0.22em] text-slate-500 mb-4">LIVE PREVIEW — A REAL SMALL-BUSINESS SITE</p>
              <Preview s={state} />
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  { icon: Languages, label: "132 languages" },
                  { icon: Volume2, label: "Read aloud · 4 modes · 0.5x–2x" },
                  { icon: Users, label: "9 one-tap profiles" },
                  { icon: Puzzle, label: "84 features in every plan" },
                ].map((f) => (
                  <div key={f.label} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-panel px-4 py-3">
                    <f.icon className="h-4 w-4 shrink-0 text-brandamber" />
                    <span className="text-xs sm:text-sm text-slate-300">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
